/**
 * Event Detail Page
 * 
 * Displays complete event information with:
 * - Full event details
 * - Check-in status (riders, coaches, volunteers)
 * - RSVP list with filtering (if available)
 * - Edit/delete buttons for admin/coach
 * 
 * Requirements: 2.4, 2.5, 3.8
 */

import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import useEventsStore from '../store/eventsStore';
import { useAuth } from '../auth/AuthProvider';
import { restClient, Rider, Group, TrailConfiguration, EventType, Event } from '../api/rest-client';
import { Card } from '../components/common/Card';
import { Button } from '../components/common/Button';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { Modal } from '../components/common/Modal';
import { LocationLink } from '../components/common/LocationLink';
import {
  isEventCanceled,
  describeNotificationOutcome,
  describeNotificationFailure,
} from '../utils/eventUpdate';
import { format } from 'date-fns';
import { RaceResultLeaderboard } from '../components/features/raceResults/RaceResultLeaderboard';
import { RaceResultModal } from '../components/features/raceResults/RaceResultModal';
import { DashboardIncidentButton } from '../components/features/incidents/DashboardIncidentButton';
import { formatRiderName } from '../utils/riderDisplay';
import { parseExpectedAttendance, getRSVPStatus, updateRSVPStatus } from '../utils/rsvpUtils';
import { filterRidersByEvent } from '../utils/eventFiltering';
import { useGroupLeaderStore } from '../store/groupLeaderStore';

interface RiderWithRSVP extends Rider {
  rsvpStatus: 'yes' | 'maybe' | 'no' | 'none';
}

/**
 * Format date for display
 */
function formatEventDate(starttime: string, endtime: string): { date: string; time: string } | { error: string } {
  // Safety check for invalid dates
  if (!starttime || !endtime) {
    return { error: 'Date not available' };
  }
  
  try {
    const start = new Date(starttime);
    const end = new Date(endtime);
    
    // Check if dates are valid
    if (isNaN(start.getTime()) || isNaN(end.getTime())) {
      return { error: 'Invalid date' };
    }
    
    const dateStr = format(start, 'EEEE, MMMM d, yyyy');
    const startTime = format(start, 'h:mm a');
    const endTime = format(end, 'h:mm a');
    
    return {
      date: dateStr,
      time: `${startTime} to ${endTime}`
    };
  } catch (error) {
    return { error: 'Date formatting error' };
  }
}

/**
 * Parse checkin data (handles both legacy and new formats)
 * Legacy format: { "rider_id": 1, "rider_id2": 1 }
 * New format: { "riders": { "name": timestamp }, "coaches": {...}, "volunteers": {...} }
 */
function parseCheckinData(checkinData: any): { riders?: Record<string, number>; coaches?: Record<string, number>; volunteers?: Record<string, number> } {
  if (!checkinData) return {};
  
  try {
    let parsed = checkinData;
    if (typeof checkinData === 'string') {
      parsed = JSON.parse(checkinData);
    }
    
    // Check if it's the new format (has riders/coaches/volunteers keys)
    if (parsed.riders || parsed.coaches || parsed.volunteers) {
      return parsed;
    }
    
    // It's the legacy format - just rider IDs as keys
    // Return empty object since we can't categorize without rider data
    return {};
  } catch {
    return {};
  }
}



/**
 * Check-in Section Component
 */
interface CheckinSectionProps {
  title: string;
  participants: Record<string, number>;
  icon: React.ReactNode;
}

function CheckinSection({ title, participants, icon }: CheckinSectionProps) {
  const count = Object.keys(participants).length;
  
  return (
    <div className="mb-6">
      <div className="flex items-center mb-3">
        {icon}
        <h3 className="text-lg font-semibold text-gray-900 ml-2">
          {title} ({count})
        </h3>
      </div>
      
      {count === 0 ? (
        <p className="text-gray-500 text-sm">No {title.toLowerCase()} checked in yet</p>
      ) : (
        <div className="space-y-2">
          {Object.entries(participants).map(([name, timestamp]) => (
            <div
              key={name}
              className="flex items-center justify-between p-3 bg-gray-50 rounded-md"
            >
              <span className="text-sm font-medium text-gray-900">{name}</span>
              <span className="text-xs text-gray-500">
                {format(new Date(timestamp * 1000), 'h:mm a')}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Event Detail Page Component
 */
export function EventDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isGroupLeader = useGroupLeaderStore(s => s.isGroupLeader);
  
  // Get event from store
  const {
    selectedEvent,
    loading,
    error,
    selectEvent,
    deleteEvent,
    clearSelectedEvent,
  } = useEventsStore();

  // Get auth state for role-based actions
  const { isAdmin, isCoach, user } = useAuth();

  // Local state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [riders, setRiders] = useState<RiderWithRSVP[]>([]);
  const [filteredRiders, setFilteredRiders] = useState<RiderWithRSVP[]>([]);
  const [groups, setGroups] = useState<Group[]>([]);
  const [loadingRiders, setLoadingRiders] = useState(false);
  const [updatingRSVP, setUpdatingRSVP] = useState<string | null>(null); // Track which rider's RSVP is being updated
  const [trailConfiguration, setTrailConfiguration] = useState<TrailConfiguration | null>(null);
  const [loadingTrailConfig, setLoadingTrailConfig] = useState(false);
  
  // Race results state
  const [raceResults, setRaceResults] = useState<any[]>([]);
  const [loadingRaceResults, setLoadingRaceResults] = useState(false);
  const [showRaceResultModal, setShowRaceResultModal] = useState(false);
  const [editingRaceResult, setEditingRaceResult] = useState<any | null>(null);
  const [eventTypes, setEventTypes] = useState<EventType[]>([]);
  
  // Filters
  const [groupFilter, setGroupFilter] = useState('');
  const [rsvpFilter, setRsvpFilter] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [attendanceFilter, setAttendanceFilter] = useState('');

  // Fetch event on mount
  useEffect(() => {
    if (id) {
      selectEvent(id);
    }
    
    fetchEventTypes();
    
    return () => {
      clearSelectedEvent();
    };
  }, [id, selectEvent, clearSelectedEvent]);

  /**
   * Fetch event types for color mapping
   */
  const fetchEventTypes = async () => {
    try {
      const types = await restClient.getEventTypes();
      setEventTypes(types);
    } catch (error) {
      // Silent error handling - will use default colors
    }
  };

  /**
   * Get color for event type badge
   */
  const getEventTypeColor = (eventTypeName: string): string => {
    const eventType = eventTypes.find(et => et.name === eventTypeName);
    return eventType?.color || '#435d7d'; // Default to primary blue
  };

  // Load riders when event is loaded
  useEffect(() => {
    if (selectedEvent) {
      loadRidersWithRSVP(selectedEvent);
    }
  }, [selectedEvent?.event_id]);

  // Load trail configuration if event has one
  useEffect(() => {
    const fetchTrailConfiguration = async () => {
      if (selectedEvent?.trail_configuration_id && selectedEvent.event_type === 'Time Trial') {
        try {
          setLoadingTrailConfig(true);
          const configs = await restClient.getTrailConfigurations();
          const config = configs.find(tc => tc.trail_id === selectedEvent.trail_configuration_id);
          setTrailConfiguration(config || null);
        } catch (error) {
          console.error('Failed to fetch trail configuration:', error);
          setTrailConfiguration(null);
        } finally {
          setLoadingTrailConfig(false);
        }
      } else {
        setTrailConfiguration(null);
      }
    };

    if (selectedEvent) {
      fetchTrailConfiguration();
    }
  }, [selectedEvent]);

  // Load race results if event is a Race
  useEffect(() => {
    const fetchRaceResults = async () => {
      if (selectedEvent?.event_type === 'Race') {
        try {
          setLoadingRaceResults(true);
          const results = await restClient.getRaceResults({ event_id: selectedEvent.event_id });
          setRaceResults(results);
        } catch (error) {
          console.error('Failed to fetch race results:', error);
          setRaceResults([]);
        } finally {
          setLoadingRaceResults(false);
        }
      }
    };

    if (selectedEvent) {
      fetchRaceResults();
    }
  }, [selectedEvent]);

  // Apply filters when they change
  useEffect(() => {
    applyFilters();
  }, [groupFilter, rsvpFilter, roleFilter, riders]);

  /**
   * Load riders and match with RSVP data
   */
  const loadRidersWithRSVP = async (event: Event) => {
    setLoadingRiders(true);
    try {
      const [ridersData, groupsData] = await Promise.all([
        restClient.getRiders(),
        restClient.getGroups()
      ]);

      // Re-read the latest event from the store in case it was updated while fetching riders
      const currentEvent = useEventsStore.getState().selectedEvent || event;

      // Get event's season (use event.season field or derive from event_starttime)
      const eventSeason = currentEvent.season || 
        new Date(currentEvent.event_starttime).getFullYear().toString();

      // Filter active riders from event's season
      // Check both primary season and seasons_active array (OR logic)
      let activeRiders = ridersData.filter(r => {
        const isActive = r.disabled !== '1' && r.disabled !== 1;
        // Check if rider is active in event's season
        // Either primary season matches OR seasons_active contains event season
        const isInSeason = r.season === eventSeason || 
          (r.seasons_active && r.seasons_active.includes(eventSeason));
        return isActive && isInSeason;
      });

      // Filter by included_rider_ids when present (Requirements 15.1, 15.2)
      activeRiders = filterRidersByEvent(activeRiders, currentEvent);

      // Parse expected attendance
      const attendance = parseExpectedAttendance(currentEvent.event_expectedAttendance);

      // Add RSVP status to each rider
      const ridersWithRSVP: RiderWithRSVP[] = activeRiders.map(rider => {
        const displayStatus = getRSVPStatus(attendance, rider.rider_id);
        const rsvpStatus = displayStatus === 'coming' ? 'yes' : 
                           displayStatus === 'maybe' ? 'maybe' :
                           displayStatus === 'not-coming' ? 'no' : 'none';
        return { ...rider, rsvpStatus };
      });

      setRiders(ridersWithRSVP);
      setGroups(groupsData);
    } catch (error) {
      console.error('Failed to load riders with RSVP:', error);
    } finally {
      setLoadingRiders(false);
    }
  };

  /**
   * Apply filters to riders list
   */
  const applyFilters = () => {
    let filtered = [...riders];

    // Group filter
    if (groupFilter) {
      filtered = filtered.filter(r => r.group === groupFilter);
    }

    // RSVP filter
    if (rsvpFilter) {
      filtered = filtered.filter(r => r.rsvpStatus === rsvpFilter);
    }

    // Role filter
    if (roleFilter) {
      if (roleFilter === 'coach') {
        filtered = filtered.filter(r => (r.type || 'Student').toLowerCase().includes('coach'));
      } else if (roleFilter === 'student') {
        filtered = filtered.filter(r => !(r.type || 'Student').toLowerCase().includes('coach'));
      }
    }

    // Sort: students first, then by name
    filtered.sort((a, b) => {
      const roleA = (a.type || 'student').toLowerCase();
      const roleB = (b.type || 'student').toLowerCase();
      
      if (roleA.includes('student') && !roleB.includes('student')) return -1;
      if (!roleA.includes('student') && roleB.includes('student')) return 1;
      
      return a.name.localeCompare(b.name);
    });

    setFilteredRiders(filtered);
  };

  /**
   * Get unique groups for filter, sorted by order
   */
  const getUniqueGroups = () => {
    const uniqueGroupNames = [...new Set(riders.map(r => r.group).filter(g => g && g !== 'N/A'))];
    return uniqueGroupNames
      .map(groupName => {
        const groupData = groups.find(g => g.name === groupName);
        return {
          name: groupName,
          order: groupData?.order || 999
        };
      })
      .sort((a, b) => a.order - b.order)
      .map(g => g.name);
  };

  /**
   * Handle edit event
   * Requirements: 2.5
   */
  const handleEdit = () => {
    navigate(`/events/${id}/edit`);
  };

  /**
   * Handle cancel event
   * Requirements: 2.5
   */
  const handleCancel = async () => {
    if (!id || !selectedEvent) return;

    // Get optional cancellation reason
    const reason = prompt(
      `Cancel "${selectedEvent.event_name}"?\n\nThis will send email and GroupMe notifications to all users.\n\nOptional: Enter a reason for cancellation:`,
      ''
    );
    
    // User clicked Cancel
    if (reason === null) return;

    try {
      // Step 1: Update event status to canceled
      await useEventsStore.getState().updateEvent(id, {
        event_id: id,
        event_status: 'canceled',
      });
      
      // Step 2: Send notifications in the background.
      //
      // We do NOT await this: with ~126 recipients the fan-out takes ~12s, and
      // the cancellation itself has already succeeded above. But we no longer
      // DISCARD the outcome -- a silently swallowed failure is how a 100%-failing
      // endpoint went unnoticed for months. (FR-9)
      const cancelledName = selectedEvent.event_name;
      restClient.sendCancelNotification(id, reason.trim() || undefined)
        .then((result) => {
          const outcome = describeNotificationOutcome(result);
          if (outcome.message) {
            alert(outcome.message);
          }
        })
        .catch(() => {
          alert(describeNotificationFailure(cancelledName));
        });
      
      // Refresh the event to show updated status
      await selectEvent(id);
      
      // Show success message
      alert(`Event "${selectedEvent.event_name}" has been canceled successfully.\n\nEmail and GroupMe notifications are being sent in the background to all users.`);
    } catch (error) {
      alert('Failed to cancel event. Please try again.');
    }
  };

  /**
   * Handle delete event
   * Requirements: 2.5
   */
  const handleDelete = async () => {
    if (!id) return;
    
    setDeleting(true);
    try {
      await deleteEvent(id);
      navigate('/events');
    } catch (error) {
      setDeleting(false);
    }
  };

  /**
   * Handle check-in navigation
   */
  const handleCheckIn = () => {
    navigate(`/checkin/${id}`);
  };

  /**
   * Handle add race result
   */
  const handleAddRaceResult = () => {
    setEditingRaceResult(null);
    setShowRaceResultModal(true);
  };

  /**
   * Handle edit race result
   */
  const handleEditRaceResult = (result: any) => {
    setEditingRaceResult(result);
    setShowRaceResultModal(true);
  };

  /**
   * Handle race result modal save
   */
  const handleRaceResultSave = async () => {
    // Refresh results
    if (selectedEvent) {
      try {
        const results = await restClient.getRaceResults({ event_id: selectedEvent.event_id });
        setRaceResults(results);
      } catch (error) {
        console.error('Failed to refresh race results:', error);
      }
    }
    setShowRaceResultModal(false);
    setEditingRaceResult(null);
  };

  if (loading && !selectedEvent) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-2 md:px-4 py-8">
        <Card>
          <div className="text-center py-12">
            <svg
              className="mx-auto h-12 w-12 text-red-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">Error loading event</h3>
            <p className="mt-1 text-sm text-gray-500">{error}</p>
            <div className="mt-6">
              <Button variant="primary" onClick={() => window.history.length > 1 ? navigate(-1) : navigate('/events')}>
                Back
              </Button>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  if (!selectedEvent) {
    return (
      <div className="container mx-auto px-2 md:px-4 py-8">
        <Card>
          <div className="text-center py-12">
            <h3 className="text-sm font-medium text-gray-900">Event not found</h3>
            <div className="mt-6">
              <Button variant="primary" onClick={() => window.history.length > 1 ? navigate(-1) : navigate('/events')}>
                Back
              </Button>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  const event = selectedEvent;
  const isCanceled = isEventCanceled(event); // null/undefined treated as active
  const hasCheckin = event.event_checkin && Object.keys(event.event_checkin).length > 0;
  
  // Check if event is past
  const now = new Date();
  const eventStart = new Date(event.event_starttime);
  const isPastEvent = eventStart < now;

  return (
    <div className="container mx-auto px-2 md:px-4 py-8">
      {/* Header */}
      <div className="mb-6">
        <Button
          variant="secondary"
          onClick={() => window.history.length > 1 ? navigate(-1) : navigate('/events')}
          className="mb-4"
        >
          ← Back
        </Button>
        
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <span 
                className="inline-block px-3 py-1 text-base font-semibold text-white rounded-full"
                style={{ backgroundColor: getEventTypeColor(event.event_type) }}
              >
                {event.event_type}
              </span>
              {isCanceled && (
                <span className="inline-block px-3 py-1 text-base font-semibold text-white bg-red-500 rounded-full">
                  Canceled
                </span>
              )}
            </div>
            <h1
              className={`text-3xl font-bold text-gray-900 ${
                isCanceled ? 'line-through' : ''
              }`}
            >
              {event.event_name}
            </h1>
          </div>
          
          <div className="flex gap-2 flex-wrap">
            <DashboardIncidentButton
              event={event}
              isCanceled={isCanceled}
              showCoachFeatures={isCoach}
              showAdminFeatures={isAdmin}
            />
            {isAdmin && (
              <>
                {!isPastEvent && (
                  <>
                    <Button variant="secondary" onClick={handleEdit}>
                      Edit
                    </Button>
                    {!isCanceled && (
                      <Button variant="danger" onClick={handleCancel}>
                        Cancel
                      </Button>
                    )}
                  </>
                )}
                <Button variant="danger" onClick={() => setShowDeleteModal(true)}>
                  Delete
                </Button>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Details */}
        <div className="lg:col-span-2 space-y-6">
          {/* Event Information */}
          <Card>
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Event Details</h2>
            
            <div className="space-y-4">
              {/* Date and Time */}
              <div className="flex items-start">
                <svg
                  className="w-6 h-6 mr-3 text-gray-400 flex-shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
                <div>
                  {(() => {
                    const formatted = formatEventDate(event.event_starttime, event.event_endtime);
                    if ('error' in formatted) {
                      return <p className="text-base text-gray-900">{formatted.error}</p>;
                    }
                    return (
                      <>
                        <p className="text-base text-gray-900">{formatted.date}</p>
                        <p className="text-base text-gray-600">{formatted.time}</p>
                      </>
                    );
                  })()}
                </div>
              </div>

              {/* Location */}
              <div className="flex items-start">
                <svg
                  className="w-6 h-6 mr-3 text-gray-400 flex-shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                </svg>
                <div className="flex-1">
                  <LocationLink
                    location={event.event_location}
                    locationName={event.event_location_name}
                    address={event.event_address}
                    className="text-base text-gray-900"
                  />
                </div>
              </div>

              {/* Trail Configuration (Time Trial events only) */}
              {event.event_type === 'Time Trial' && (
                <div className="flex items-start">
                  <svg
                    className="w-6 h-6 mr-3 text-gray-400 flex-shrink-0"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7"
                    />
                  </svg>
                  <div>
                    <p className="text-base font-medium text-gray-700">Trail Configuration</p>
                    {loadingTrailConfig ? (
                      <p className="text-base text-gray-600">Loading...</p>
                    ) : trailConfiguration ? (
                      <p className="text-base text-gray-900">
                        {trailConfiguration.trail_name} ({trailConfiguration.direction})
                        {trailConfiguration.is_default && (
                          <span className="ml-2 text-xs text-primary-600 font-medium">Default</span>
                        )}
                      </p>
                    ) : event.trail_configuration_id ? (
                      <p className="text-base text-gray-600">Trail configuration not found</p>
                    ) : (
                      <p className="text-base text-gray-600">No trail configured</p>
                    )}
                  </div>
                </div>
              )}

              {/* Official Results URL (Race events only) */}
              {event.event_type === 'Race' && event.official_results_url && (
                <div className="flex items-start">
                  <svg
                    className="w-6 h-6 mr-3 text-gray-400 flex-shrink-0"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z"
                    />
                  </svg>
                  <div>
                    <p className="text-base font-medium text-gray-700">Official Results</p>
                    <a
                      href={event.official_results_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-base text-primary-600 hover:text-primary-700 hover:underline inline-flex items-center"
                    >
                      View Official Results →
                    </a>
                  </div>
                </div>
              )}
            </div>
          </Card>

          {/* Race Results (Race events only) */}
          {event.event_type === 'Race' && (
            <Card>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-gray-900">Race Results</h2>
                {(isAdmin || isCoach) && (
                  <Button variant="primary" onClick={handleAddRaceResult}>
                    Add Result
                  </Button>
                )}
              </div>
              
              {loadingRaceResults ? (
                <div className="flex justify-center py-8">
                  <LoadingSpinner size="md" />
                </div>
              ) : (
                <RaceResultLeaderboard
                  results={raceResults}
                  riders={riders}
                  eventName={event.event_name}
                  showRecordButton={false}
                  onEditClick={(isAdmin || isCoach) ? handleEditRaceResult : undefined}
                  onDeleteSuccess={async () => {
                    // Refresh results after delete
                    const results = await restClient.getRaceResults({ event_id: event.event_id });
                    setRaceResults(results);
                  }}
                />
              )}
            </Card>
          )}

          {/* Check-in Status - Order 2 on mobile, stays in main content on desktop */}
          {hasCheckin && !isPastEvent && (isAdmin || isCoach) && (
            <Card className="lg:hidden">
              <div className="mb-4">
                <h2 className="text-xl font-semibold text-gray-900 mb-3">Check-in Status</h2>
                <Button 
                  onClick={handleCheckIn}
                  style={{ backgroundColor: '#28a745', color: 'white' }}
                  className="w-full"
                >
                  Rider Check-in
                </Button>
              </div>

              {/* Riders */}
              {(() => {
                const checkinData = parseCheckinData(event.event_checkin);
                return checkinData.riders && (
                  <CheckinSection
                    title="Riders"
                    participants={checkinData.riders}
                    icon={
                      <svg
                        className="w-5 h-5 text-primary-500"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z" />
                      </svg>
                    }
                  />
                );
              })()}

              {/* Coaches */}
              {(() => {
                const checkinData = parseCheckinData(event.event_checkin);
                return checkinData.coaches && (
                  <CheckinSection
                    title="Coaches"
                    participants={checkinData.coaches}
                    icon={
                      <svg
                        className="w-5 h-5 text-secondary-500"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3zM6 8a2 2 0 11-4 0 2 2 0 014 0zM16 18v-3a5.972 5.972 0 00-.75-2.906A3.005 3.005 0 0119 15v3h-3zM4.75 12.094A5.973 5.973 0 004 15v3H1v-3a3 3 0 013.75-2.906z" />
                      </svg>
                    }
                  />
                );
              })()}

              {/* Volunteers */}
              {(() => {
                const checkinData = parseCheckinData(event.event_checkin);
                return checkinData.volunteers && (
                  <CheckinSection
                    title="Volunteers"
                    participants={checkinData.volunteers}
                    icon={
                      <svg
                        className="w-5 h-5 text-yellow-500"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z"
                          clipRule="evenodd"
                        />
                      </svg>
                    }
                  />
                );
              })()}
            </Card>
          )}

          {/* RSVP Section - For users to RSVP their riders */}
          {(() => {
            const now = new Date();
            const eventStart = new Date(event.event_starttime);
            const isFutureEvent = eventStart > now;
            
            // Only show RSVP section for future events
            if (!isFutureEvent) return null;
            
            // Filter to only riders associated with the current user
            const associatedRiders = riders.filter(rider => {
              if (!rider.associatedUsers) return false;
              
              try {
                const users = JSON.parse(rider.associatedUsers);
                return users.some((u: any) => 
                  u.user_id === user?.username || 
                  u.user_id === user?.email ||
                  u.email === user?.email ||
                  u.email === user?.username
                );
              } catch {
                return false;
              }
            });
            
            // Don't show section if user has no associated riders
            if (associatedRiders.length === 0) return null;
            
            return (
              <Card>
                <h2 className="text-xl font-semibold text-gray-900 mb-4">RSVP Status</h2>
                {loadingRiders ? (
                  <div className="flex justify-center py-8">
                    <LoadingSpinner size="md" />
                  </div>
                ) : (
                  <div className="space-y-3">
                    {[...associatedRiders]
                      .sort((a, b) => formatRiderName(a).localeCompare(formatRiderName(b)))
                      .map(rider => {
                      const attendance = parseExpectedAttendance(event.event_expectedAttendance);
                      const displayStatus = getRSVPStatus(attendance, rider.rider_id);
                      const rsvpStatus = displayStatus === 'coming' ? 1 : 
                                         displayStatus === 'maybe' ? 2 :
                                         displayStatus === 'not-coming' ? 0 : -1;
                      
                      const getRSVPIcon = (status: number) => {
                        switch (status) {
                          case 1: // Coming
                            return (
                              <svg className="w-6 h-6 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                              </svg>
                            );
                          case 2: // Maybe
                            return (
                              <svg className="w-6 h-6 text-orange-500" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                              </svg>
                            );
                          case 0: // Not coming
                            return (
                              <svg className="w-6 h-6 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                              </svg>
                            );
                          default: // Unknown
                            return (
                              <svg className="w-6 h-6 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                              </svg>
                            );
                        }
                      };
                      
                      const getRSVPLabel = (status: number) => {
                        switch (status) {
                          case 1: return 'Yes';
                          case 2: return 'Maybe';
                          case 0: return 'No';
                          default: return 'RSVP';
                        }
                      };
                      
                      const handleRSVPClick = async () => {
                        setUpdatingRSVP(rider.rider_id);
                        try {
                          const result = await updateRSVPStatus(event, rider.rider_id, displayStatus);
                          if (result.success) {
                            const updatedEvent = {
                              ...event,
                              event_expectedAttendance: result.updatedAttendance
                            };
                            useEventsStore.setState(state => ({
                              events: state.events.map(e => 
                                e.event_id === event.event_id ? updatedEvent : e
                              ),
                              selectedEvent: updatedEvent
                            }));
                          } else {
                            alert('Failed to update RSVP. Please try again.');
                            await selectEvent(event.event_id);
                          }
                        } catch (err) {
                          alert('Failed to update RSVP. Please try again.');
                          await selectEvent(event.event_id);
                        } finally {
                          setUpdatingRSVP(null);
                        }
                      };
                      
                      const isUpdating = updatingRSVP === rider.rider_id;

                      const rsvpButtonStyles = (() => {
                        switch (rsvpStatus) {
                          case 1: // Coming
                            return 'bg-green-50 border-2 border-green-500 text-green-700 hover:bg-green-100 shadow-sm';
                          case 2: // Maybe
                            return 'bg-orange-50 border-2 border-orange-400 text-orange-700 hover:bg-orange-100 shadow-sm';
                          case 0: // Not coming
                            return 'bg-red-50 border-2 border-red-400 text-red-700 hover:bg-red-100 shadow-sm';
                          default: // No response
                            return 'bg-primary-500 border-2 border-primary-500 text-white hover:bg-primary-600 shadow-lg ring-2 ring-primary-300 ring-offset-1 animate-pulse';
                        }
                      })();
                      
                      return (
                        <div key={rider.rider_id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                          <div>
                            <p className="font-medium text-gray-900">{formatRiderName(rider)}{isGroupLeader(rider.rider_id) ? ' ⭐' : ''}</p>
                            <p className="text-sm text-gray-600">{rider.group}</p>
                          </div>
                          <button
                            onClick={handleRSVPClick}
                            className={`flex flex-col items-center gap-1.5 w-[100px] h-[70px] justify-center flex-shrink-0 rounded-lg font-medium transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed ${rsvpButtonStyles}`}
                            title={getRSVPLabel(rsvpStatus)}
                            disabled={isUpdating}
                          >
                            {isUpdating ? (
                              <>
                                <svg className="animate-spin h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                <span className="text-xs text-center leading-tight">Updating...</span>
                              </>
                            ) : (
                              <>
                                {getRSVPIcon(rsvpStatus)}
                                <span className="text-sm font-semibold text-center leading-tight">{getRSVPLabel(rsvpStatus)}</span>
                              </>
                            )}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </Card>
            );
          })()}

          {/* RSVP List (Future Events) or Actual Attendance List (Past Events) */}
          {(() => {
            // Check if event is past
            const now = new Date();
            const eventStart = new Date(event.event_starttime);
            const isPastEvent = eventStart < now;
            
            // For past events, show actual attendance list
            if (isPastEvent) {
              // Parse check-in data (legacy format: { "rider_id": 1 })
              let checkinData: any = {};
              try {
                checkinData = typeof event.event_checkin === 'string' 
                  ? JSON.parse(event.event_checkin) 
                  : event.event_checkin || {};
              } catch {
                checkinData = {};
              }
              
              // Get list of checked-in rider IDs
              const checkedInRiderIds = Object.keys(checkinData).filter(id => checkinData[id] === 1);
              
              // Filter riders to those who checked in and those who didn't
              const attendedRiders = riders.filter(r => checkedInRiderIds.includes(r.rider_id));
              
              // Count by role
              const attendedCoaches = attendedRiders.filter(r => (r.type || 'Student').toLowerCase().includes('coach'));
              const attendedStudents = attendedRiders.filter(r => !(r.type || 'Student').toLowerCase().includes('coach'));
              
              const sortedGroups = getUniqueGroups();

              return (
                <Card>
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-xl font-semibold text-gray-900">
                      Actual Attendance
                    </h2>
                    {isAdmin && !isCanceled && (
                      <Button
                        variant="primary"
                        data-testid="edit-attendance-button"
                        onClick={handleCheckIn}
                      >
                        Edit Attendance
                      </Button>
                    )}
                  </div>
                  
                  {/* Summary */}
                  <div className="grid grid-cols-3 gap-3 mb-4 pb-4 border-b border-gray-200">
                    <div className="flex flex-col items-center p-3 bg-blue-50 rounded-md">
                      <span className="text-sm font-medium text-gray-700 mb-1">Students</span>
                      <span className="text-2xl font-bold text-blue-600">{attendedStudents.length}</span>
                    </div>
                    <div className="flex flex-col items-center p-3 bg-purple-50 rounded-md">
                      <span className="text-sm font-medium text-gray-700 mb-1">Coaches</span>
                      <span className="text-2xl font-bold text-purple-600">{attendedCoaches.length}</span>
                    </div>
                    <div className="flex flex-col items-center p-3 bg-green-50 rounded-md">
                      <span className="text-sm font-medium text-gray-700 mb-1">Total</span>
                      <span className="text-2xl font-bold text-green-600">{attendedRiders.length}</span>
                    </div>
                  </div>

                  {riders.length === 0 ? (
                    <p className="text-sm text-gray-500 text-center py-8">
                      No riders found
                    </p>
                  ) : (
                    <>
                      {/* Filters */}
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Filter by Group:</label>
                          <select
                            value={groupFilter}
                            onChange={(e) => setGroupFilter(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                          >
                            <option value="">All Groups</option>
                            {sortedGroups.map(group => (
                              <option key={group} value={group}>{group}</option>
                            ))}
                          </select>
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Filter by Attendance:</label>
                          <select
                            value={attendanceFilter}
                            onChange={(e) => setAttendanceFilter(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                          >
                            <option value="">All</option>
                            <option value="attended">Attended</option>
                            <option value="did-not-attend">Did Not Attend</option>
                          </select>
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Filter by Role:</label>
                          <select
                            value={roleFilter}
                            onChange={(e) => setRoleFilter(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                          >
                            <option value="">All Roles</option>
                            <option value="coach">Coaches</option>
                            <option value="student">Students</option>
                          </select>
                        </div>
                      </div>

                      {/* Riders List */}
                      {loadingRiders ? (
                        <div className="flex justify-center py-8">
                          <LoadingSpinner size="md" />
                        </div>
                      ) : (() => {
                        // Apply group and role filters first
                        let displayRiders = filteredRiders;
                        
                        // Then apply attendance filter
                        if (attendanceFilter === 'attended') {
                          displayRiders = displayRiders.filter(r => checkedInRiderIds.includes(r.rider_id));
                        } else if (attendanceFilter === 'did-not-attend') {
                          displayRiders = displayRiders.filter(r => !checkedInRiderIds.includes(r.rider_id));
                        }
                        
                        return displayRiders.length === 0 ? (
                          <p className="text-sm text-gray-500 text-center py-8">
                            No riders match the filters
                          </p>
                        ) : (
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                            {displayRiders.map(rider => {
                              const attended = checkedInRiderIds.includes(rider.rider_id);
                              return (
                                <div
                                  key={rider.rider_id}
                                  className="flex items-center justify-between p-3 hover:bg-gray-50 rounded-md border border-gray-200"
                                >
                                  <div className="flex-1 min-w-0">
                                    <p className="font-medium text-gray-900 truncate">{formatRiderName(rider)}{isGroupLeader(rider.rider_id) ? ' ⭐' : ''}</p>
                                    <p className="text-xs text-gray-500">{rider.group || 'N/A'}</p>
                                  </div>
                                  <span className={`ml-2 px-2 py-1 rounded text-xs font-medium whitespace-nowrap ${
                                    attended 
                                      ? 'bg-green-100 text-green-800' 
                                      : 'bg-red-100 text-red-800'
                                  }`}>
                                    {attended ? 'Attended' : 'Did Not Attend'}
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        );
                      })()}
                    </>
                  )}
                </Card>
              );
            }
            
            // For future events, show RSVP list
            if (!event.event_expectedAttendance) return null;
            
            const attendance = parseExpectedAttendance(event.event_expectedAttendance);
            const hasAttendance = Object.keys(attendance).length > 0;
            
            if (!hasAttendance) return null;
            
            // Count RSVPs
            const yesCount = riders.filter(r => r.rsvpStatus === 'yes').length;
            const maybeCount = riders.filter(r => r.rsvpStatus === 'maybe').length;
            const noCount = riders.filter(r => r.rsvpStatus === 'no').length;
            const noResponseCount = riders.filter(r => r.rsvpStatus === 'none').length;
            
            const sortedGroups = getUniqueGroups();

            return (
              <Card>
                <h2 className="text-xl font-semibold text-gray-900 mb-4">
                  RSVP List
                </h2>
                
                {/* Summary */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4 pb-4 border-b border-gray-200">
                  <div className="flex flex-col items-center p-3 bg-green-50 rounded-md">
                    <span className="text-sm font-medium text-gray-700 mb-1">Yes</span>
                    <span className="text-2xl font-bold text-green-600">{yesCount}</span>
                  </div>
                  <div className="flex flex-col items-center p-3 bg-orange-50 rounded-md">
                    <span className="text-sm font-medium text-gray-700 mb-1">Maybe</span>
                    <span className="text-2xl font-bold text-orange-600">{maybeCount}</span>
                  </div>
                  <div className="flex flex-col items-center p-3 bg-red-50 rounded-md">
                    <span className="text-sm font-medium text-gray-700 mb-1">No</span>
                    <span className="text-2xl font-bold text-red-600">{noCount}</span>
                  </div>
                  <div className="flex flex-col items-center p-3 bg-gray-50 rounded-md">
                    <span className="text-sm font-medium text-gray-700 mb-1">No Response</span>
                    <span className="text-2xl font-bold text-gray-900">{noResponseCount}</span>
                  </div>
                </div>

                {/* Filters */}
                {riders.length > 0 && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Filter by Group:</label>
                      <select
                        value={groupFilter}
                        onChange={(e) => setGroupFilter(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                      >
                        <option value="">All Groups</option>
                        {sortedGroups.map(group => (
                          <option key={group} value={group}>{group}</option>
                        ))}
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Filter by RSVP:</label>
                      <select
                        value={rsvpFilter}
                        onChange={(e) => setRsvpFilter(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                      >
                        <option value="">All RSVP</option>
                        <option value="yes">Yes</option>
                        <option value="maybe">Maybe</option>
                        <option value="no">No</option>
                        <option value="none">No Response</option>
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Filter by Role:</label>
                      <select
                        value={roleFilter}
                        onChange={(e) => setRoleFilter(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                      >
                        <option value="">All Roles</option>
                        <option value="coach">Coaches</option>
                        <option value="student">Students</option>
                      </select>
                    </div>
                  </div>
                )}

                {/* Riders List */}
                {loadingRiders ? (
                  <div className="flex justify-center py-8">
                    <LoadingSpinner size="md" />
                  </div>
                ) : filteredRiders.length === 0 ? (
                  <p className="text-sm text-gray-500 text-center py-8">
                    {riders.length === 0 ? 'No RSVP responses yet' : 'No riders match the filters'}
                  </p>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {filteredRiders.map(rider => (
                      <div
                        key={rider.rider_id}
                        className="flex items-center justify-between p-3 hover:bg-gray-50 rounded-md border border-gray-200"
                      >
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-gray-900 truncate">{formatRiderName(rider)}{isGroupLeader(rider.rider_id) ? ' ⭐' : ''}</p>
                          <p className="text-xs text-gray-500">{rider.group || 'N/A'}</p>
                        </div>
                        <span className={`ml-2 px-2 py-1 rounded text-xs font-medium whitespace-nowrap ${
                          rider.rsvpStatus === 'yes' 
                            ? 'bg-green-100 text-green-800' 
                            : rider.rsvpStatus === 'maybe'
                            ? 'bg-orange-100 text-orange-800'
                            : rider.rsvpStatus === 'no'
                            ? 'bg-red-100 text-red-800'
                            : 'bg-gray-100 text-gray-600'
                        }`}>
                          {rider.rsvpStatus === 'yes' ? 'Yes' : rider.rsvpStatus === 'maybe' ? 'Maybe' : rider.rsvpStatus === 'no' ? 'No' : 'No Response'}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            );
          })()}
        </div>

        {/* Sidebar - Check-in Status (desktop only) */}
        <div className="space-y-6">
          {/* Check-in Status - Hidden on mobile, shown on desktop */}
          {hasCheckin && !isPastEvent && (isAdmin || isCoach) && (
            <Card className="hidden lg:block">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Check-in Status</h3>
                <Button 
                  onClick={handleCheckIn}
                  style={{ backgroundColor: '#28a745', color: 'white' }}
                >
                  Rider Check-in
                </Button>
              </div>

              {/* Riders */}
              {(() => {
                const checkinData = parseCheckinData(event.event_checkin);
                return checkinData.riders && (
                  <CheckinSection
                    title="Riders"
                    participants={checkinData.riders}
                    icon={
                      <svg
                        className="w-5 h-5 text-primary-500"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z" />
                      </svg>
                    }
                  />
                );
              })()}

              {/* Coaches */}
              {(() => {
                const checkinData = parseCheckinData(event.event_checkin);
                return checkinData.coaches && (
                  <CheckinSection
                    title="Coaches"
                    participants={checkinData.coaches}
                    icon={
                      <svg
                        className="w-5 h-5 text-secondary-500"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3zM6 8a2 2 0 11-4 0 2 2 0 014 0zM16 18v-3a5.972 5.972 0 00-.75-2.906A3.005 3.005 0 0119 15v3h-3zM4.75 12.094A5.973 5.973 0 004 15v3H1v-3a3 3 0 013.75-2.906z" />
                      </svg>
                    }
                  />
                );
              })()}

              {/* Volunteers */}
              {(() => {
                const checkinData = parseCheckinData(event.event_checkin);
                return checkinData.volunteers && (
                  <CheckinSection
                    title="Volunteers"
                    participants={checkinData.volunteers}
                    icon={
                      <svg
                        className="w-5 h-5 text-yellow-500"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z"
                          clipRule="evenodd"
                        />
                      </svg>
                    }
                  />
                );
              })()}
            </Card>
          )}
        </div>
      </div>

      {/* Race Result Modal */}
      {showRaceResultModal && selectedEvent && (
        <RaceResultModal
          isOpen={showRaceResultModal}
          onClose={() => {
            setShowRaceResultModal(false);
            setEditingRaceResult(null);
          }}
          onSuccess={handleRaceResultSave}
          eventId={selectedEvent.event_id}
          eventName={selectedEvent.event_name}
          editMode={!!editingRaceResult}
          existingResult={editingRaceResult}
        />
      )}

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Delete Event"
      >
        <div className="space-y-4">
          <p className="text-gray-700">
            Are you sure you want to delete "{event.event_name}"? This action cannot be undone.
          </p>
          <div className="flex gap-3 justify-end">
            <Button
              variant="secondary"
              onClick={() => setShowDeleteModal(false)}
              disabled={deleting}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={handleDelete}
              disabled={deleting}
            >
              {deleting ? 'Deleting...' : 'Delete Event'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

export default EventDetail;
